/* eslint-disable import/no-default-export */
import React, { useEffect } from 'react';
import { generatePath, Switch } from 'react-router-dom';

import { SSVATasksPage } from 'sellDirect/routes/virtual-assessments/ssva/SSVATasksPage';
import { TaskUploadPage } from 'sellDirect/routes/virtual-assessments/ssva/TaskUploadPage';
import { SchedulePage } from 'sellDirect/routes/virtual-assessments/Schedule';
import { VAQuestionsPage } from 'sellDirect/routes/virtual-assessments/VAQuestions';
import { GenericErrorPage } from 'shared/routes/errors/GenericErrorPage';
import {
  getHubSellingRoute,
  getHubORVAScheduleRoute,
  getHubOverviewRoute,
  getHubSSVAUploadBackyardRoute,
  getHubSSVAUploadFrontyardRoute,
  getHubSSVAUploadInteriorRoute,
  getHubSSVAUploadRoute,
  getHubSSVAUploadWholeHomeRoute,
  getHubSSVAUploadSellerDisclosureDocumentRoute,
  getHubVirtualAssessmentQuestionsRoute,
  getHomeConditionRoute,
  getExteriorAssessmentSelfSchedulingRoute,
} from 'shared/helpers/routes';

import { OdProtosWebData_RecordedFeature as RecordedFeature } from '__generated__/globalTypes';
import { getPreClosingOffer, useOfferContext } from 'sellDirect/OfferContext';
import { AddContextualData } from '@opendoor/observability';
import { useObservability } from 'shared/helpers/observability';
import { SellDirectClosingRouter } from 'sellDirect/routes/SellDirectClosingRouter';
import { SellingPage } from 'sellDirect/routes/sell/SellingPage';
import { hasAnsweredOccupancyQuestion } from 'sellDirect/helpers/virtualAssessmentQuestions';
import { HomeConditionPage } from 'sellDirect/routes/price-adjustment/HomeConditionPage';
import { useExperiments } from 'sellDirect/helpers/ExperimentProvider';
import { Redirect, Route } from 'platform/react-router';

import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { LoadingPage } from 'platform/components/LoadingPage';
import { safelyParseWebviewMessage } from 'shared/helpers/rnBridge';
import { isOpendoorAppEvoWebView } from 'shared/helpers/globals';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { ExteriorAssessmentPage } from 'sellDirect/routes/exterior-assessment/ExteriorAssessmentPage';
import { ExteriorAssessmentSelfScheduling } from 'sellDirect/routes/legacy/exteriorAssessmentSelfScheduling';

/** Handles initial conditional logic for seller direct routes */
export default function SellDirectRouter() {
  const {
    assertions: { isExteriorAssessmentInHubOn },
  } = useExperimentationContext();
  const { addressUUID } = useQualificationContext();
  const {
    canSeeClosingDashInHub,
    loading: offerLoading,
    experimentationData: { shouldSeeSsvaNooExperience },
    offer,
    refetch: refetchOffer,
  } = useOfferContext();
  const { priceAdjustment, loading: experimentLoading } = useExperiments();
  const { trackError } = useObservability();
  const conditionalRoutes = [];

  // Handles messages from Opendoor Mobile app
  useEffect(() => {
    const handleWebviewMessage = async (message: MessageEvent<string>): Promise<void> => {
      const parseData = safelyParseWebviewMessage(message);
      if (parseData?.type.eventName === 'refresh') {
        await refetchOffer();
      }
    };

    if (isOpendoorAppEvoWebView) {
      /**
       * have to add `true` to the third option for this to work in both
       * android and iOS. Otherwise it will only work in iOS
       * https://github.com/react-native-webview/react-native-webview/issues/356#issuecomment-1290912572
       */
      window.addEventListener('message', handleWebviewMessage, true);
      return () => {
        window.removeEventListener('message', handleWebviewMessage, true);
      };
    }
  }, [refetchOffer]);

  if (offerLoading || experimentLoading) {
    return <LoadingPage from="SellDirectRouter" reason="loading" />;
  }

  if (!offer) {
    trackError('selling-page-missing-latest-offer', undefined);
    return <GenericErrorPage from="hub-missing-latest-offer" />;
  }

  if (canSeeClosingDashInHub) {
    return (
      <AddContextualData
        data={{
          webOfferToken: offer.leadToken,
          sentryZero: true, // errors with this tag will alert us immediately
          resource: 'closing-dashboard', // not really sure if this is needed
        }}
      >
        <SellDirectClosingRouter />
      </AddContextualData>
    );
  }

  const preClosingOffer = getPreClosingOffer(offer);

  if (!preClosingOffer) {
    trackError('selling-page-missing-latest-offer', undefined);
    return <GenericErrorPage from="hub-missing-latest-offer" />;
  }

  if (!offer.isExpired) {
    if (priceAdjustment.showEntryPoint) {
      conditionalRoutes.push(
        <Route
          exact
          key={getHomeConditionRoute()}
          path={getHomeConditionRoute()}
          render={() => <HomeConditionPage />}
        />,
      );
    }

    // TODO: Only mount when the offer is in a state that requires an EA to be scheduled
    conditionalRoutes.push(
      <Route
        exact
        key={getExteriorAssessmentSelfSchedulingRoute()}
        path={getExteriorAssessmentSelfSchedulingRoute()}
        render={() =>
          isExteriorAssessmentInHubOn ? (
            <ExteriorAssessmentPage />
          ) : (
            <ExteriorAssessmentSelfScheduling />
          )
        }
      />,
    );

    // if offer is not expired, SSVA routes should always be available
    conditionalRoutes.push(
      <Route
        exact
        key={getHubSSVAUploadRoute()}
        path={getHubSSVAUploadRoute()}
        render={() => <SSVATasksPage offer={preClosingOffer} />}
      />,
    );

    // Route for the SSVA Targeting Experiment
    // Should only be visible if user has not answered the occupancy question
    // and has not completed VA
    if (
      shouldSeeSsvaNooExperience &&
      !hasAnsweredOccupancyQuestion(preClosingOffer.occupancyAtVa) &&
      (offer.state === 'Sent' || offer.state === 'VAInProgress')
    ) {
      conditionalRoutes.push(
        <Route
          exact
          key={getHubVirtualAssessmentQuestionsRoute()}
          path={getHubVirtualAssessmentQuestionsRoute()}
          render={() => <VAQuestionsPage offer={offer} />}
        />,
      );
    }

    if (offer.shouldSeeAgentExperience) {
      conditionalRoutes.push(
        offer.lead.isListed ? (
          <Route
            exact
            key={getHubSSVAUploadSellerDisclosureDocumentRoute()}
            path={getHubSSVAUploadSellerDisclosureDocumentRoute()}
            render={() => (
              <TaskUploadPage
                offer={preClosingOffer}
                recordedFeature={RecordedFeature.SELLER_DISCLOSURE_DOCUMENT}
              />
            )}
          />
        ) : (
          <Route
            exact
            key={getHubSSVAUploadWholeHomeRoute()}
            path={getHubSSVAUploadWholeHomeRoute()}
            render={() => (
              <TaskUploadPage
                offer={preClosingOffer}
                recordedFeature={RecordedFeature.WHOLE_HOME}
              />
            )}
          />
        ),
      );
    } else {
      conditionalRoutes.push(
        <Route
          exact
          key={getHubSSVAUploadInteriorRoute()}
          path={getHubSSVAUploadInteriorRoute()}
          render={() => (
            <TaskUploadPage offer={preClosingOffer} recordedFeature={RecordedFeature.INTERIOR} />
          )}
        />,
        <Route
          exact
          key={getHubSSVAUploadBackyardRoute()}
          path={getHubSSVAUploadBackyardRoute()}
          render={() => (
            <TaskUploadPage offer={preClosingOffer} recordedFeature={RecordedFeature.BACKYARD} />
          )}
        />,
        <Route
          exact
          key={getHubSSVAUploadFrontyardRoute()}
          path={getHubSSVAUploadFrontyardRoute()}
          render={() => (
            <TaskUploadPage offer={preClosingOffer} recordedFeature={RecordedFeature.FRONTYARD} />
          )}
        />,
      );
      // if ORVA is not scheduled and VA is not completed, allow to see the /sell/schedule route to get started with ORVA or SSVA
      // If Buy+List is enabled, we don't worry if the offer is expired, we allow scheduling. The offer will get auto-refreshed when booking the ORVA
      if (
        (preClosingOffer.state === 'Sent' || preClosingOffer.state === 'VAInProgress') &&
        !preClosingOffer.isExpired
      ) {
        conditionalRoutes.push(
          <Route
            exact
            key={getHubORVAScheduleRoute()}
            path={getHubORVAScheduleRoute()}
            render={() => <SchedulePage offer={preClosingOffer} />}
          />,
        );
      }
    }
  }

  return (
    <Switch>
      {conditionalRoutes}
      <Route
        exact
        path={getHubSellingRoute()}
        render={() => <SellingPage offer={preClosingOffer} />}
      />
      {/* if route doesn't matches any of the sub routes, fallback to TODO page */}
      <Route>
        <Redirect to={generatePath(getHubOverviewRoute(), { addressUUID })} />
      </Route>
    </Switch>
  );
}
